import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { getUniqueBackgrounds } from "../apis/exercisesApi";
import BackgroundUploadModal from "./BackgroundUploadModal";
import axios from "axios"; // Added for API calls

const BackgroundSelector = ({ onSelect, onClose }) => {
  const [backgrounds, setBackgrounds] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const FALLBACK_IMAGE = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYGD4z8ABAA2g8b9HAAAAAElFTkSuQmCC";

  const fetchBackgrounds = async () => {
    try {
      const data = await getUniqueBackgrounds();
      console.log("Fetched backgrounds:", data);
      setBackgrounds(data || []);
      setLoading(false);
    } catch (error) {
      Swal.fire("Error", "Failed to load backgrounds", "error");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackgrounds();
  }, []);

  const getFileNameFromUrl = (url) => {
    if (!url || typeof url !== "string") return "Unknown Image";
    const parts = url.split("/");
    return parts[parts.length - 1] || "Unknown Image";
  };

  const handleSelect = (image) => {
    setPreviewUrl(image);
    setSelectedFile(image);
  };

  const handleKeyDown = (e, image) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(image);
    }
  };

  const handleOk = () => {
    if (selectedFile) {
      // If it's already a full URL, use it directly
      if (selectedFile.startsWith("http")) {
        onSelect(selectedFile);
        onClose();
      } 
      // If it's a relative path, convert it to a full URL
      else if (selectedFile.startsWith("/images/")) {
        const fullUrl = `${window.location.origin}${selectedFile}`;
        onSelect(fullUrl);
        onClose();
      }
      // If it's just the filename, construct the full URL
      else if (selectedFile.startsWith("images/")) {
        const fullUrl = `${window.location.origin}/${selectedFile}`;
        onSelect(fullUrl);
        onClose();
      }
      else {
        Swal.fire("Error", "Invalid background path selected", "error");
      }
    } else {
      Swal.fire("Warning", "Please select a background", "warning");
    }
  };

  const handleClear = async () => {
    if (!selectedFile) {
      Swal.fire("Warning", "No background selected to delete", "warning");
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This will delete the selected background image.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#541AC9",
        cancelButtonColor: "#dc2626",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        // Call the deleteBackgroundImage API with path as query parameter
        await axios.delete("/api/backgrounds/by-path", {
          params: { path: selectedFile },
        });
        Swal.fire("Success", "Background image deleted successfully", "success");

        // Clear the selection and re-fetch backgrounds
        setSelectedFile(null);
        setPreviewUrl(null);
        await fetchBackgrounds();
      }
    } catch (error) {
      console.error("Error deleting background:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete background image",
        "error"
      );
    }
  };

  const handleNewUpload = async (newImageUrl) => {
    console.log("New image URL in handleNewUpload:", newImageUrl);
    setShowUploadModal(false);
    await fetchBackgrounds(); // Re-fetch backgrounds to ensure the list is up-to-date
    setSelectedFile(newImageUrl);
    setPreviewUrl(newImageUrl);
    Swal.fire("Success", "Background uploaded and selected", "success");
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          color: "#fff",
          fontSize: "1.5rem",
        }}
      >
        Loading backgrounds...
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "80%",
          height: "80%",
          backgroundColor: "#fff",
          padding: "40px",
          borderRadius: "20px",
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.4)",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <h3
          style={{
            fontSize: "2rem",
            fontWeight: "600",
            color: "#1f2937",
            textAlign: "center",
            marginBottom: "30px",
          }}
        >
          Select Background Image
        </h3>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            marginBottom: "30px",
            justifyContent: "center",
          }}
        >
          {backgrounds.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: "#1f2937",
                fontSize: "1.2rem",
              }}
            >
              No background images available.
            </p>
          ) : (
            backgrounds.map((image, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(image)}
                onKeyDown={(e) => handleKeyDown(e, image)}
                aria-label={`Select background ${getFileNameFromUrl(image)}`}
                style={{
                  border: selectedFile === image ? "3px solid #541AC9" : "2px solid #ddd",
                  padding: "10px",
                  cursor: "pointer",
                  borderRadius: "10px",
                  transition: "border-color 0.2s ease, transform 0.2s ease",
                  transform: selectedFile === image ? "scale(1.05)" : "scale(1)",
                  backgroundColor: selectedFile === image ? "#f3e8ff" : "#fff",
                  boxShadow: selectedFile === image ? "4px 4px 12px rgba(0, 0, 0, 0.3)" : "none",
                }}
              >
                <img
                  src={image}
                  alt={getFileNameFromUrl(image)}
                  style={{
                    width: "150px",
                    height: "150px",
                    objectFit: "cover",
                    borderRadius: "5px",
                  }}
                  onError={(e) => {
                    console.error(`Failed to load ${image}`);
                    e.target.src = FALLBACK_IMAGE;
                  }}
                />
                <p
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    color: "#1f2937",
                    fontSize: "1rem",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getFileNameFromUrl(image)}
                </p>
              </div>
            ))
          )}
        </div>
        {previewUrl && (
          <div style={{ marginBottom: "30px", textAlign: "center" }}>
            <p
              style={{
                fontSize: "1.25rem",
                color: "#1f2937",
                marginBottom: "10px",
              }}
            >
              Selected Preview:
            </p>
            <img
              src={previewUrl}
              alt="Selected background"
              style={{
                width: "300px",
                height: "300px",
                objectFit: "cover",
                borderRadius: "10px",
                border: "2px solid #541AC9",
              }}
              onError={(e) => {
                console.error(`Failed to load preview ${previewUrl}`);
                e.target.src = FALLBACK_IMAGE;
              }}
            />
          </div>
        )}
        <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            aria-label="Upload new background"
            style={{
              padding: "10px 20px",
              backgroundColor: "#541AC9",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              
              fontSize: "1.1rem",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              boxShadow: "4px 4px 12px rgba(0, 0, 0, 0.3)",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#3A1D74")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#541AC9")}
          >
            Upload
          </button>
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear selection"
            disabled={!selectedFile}
            style={{
              padding: "10px 20px",
              backgroundColor: selectedFile ? "#f97316" : "#d1d5db",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "1.1rem",
              cursor: selectedFile ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              boxShadow: selectedFile ? "4px 4px 12px rgba(0, 0, 0, 0.3)" : "none",
            }}
            onMouseOver={(e) => selectedFile && (e.target.style.backgroundColor = "#ea580c")}
            onMouseOut={(e) => selectedFile && (e.target.style.backgroundColor = "#f97316")}
          >
            Clear
          </button>
          <button
            onClick={handleOk}
            aria-label="Confirm selection"
            style={{
              padding: "10px 20px",
              backgroundColor: "#541AC9",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "1.1rem",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              boxShadow: "4px 4px 12px rgba(0, 0, 0, 0.3)",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#3A1D74")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#541AC9")}
          >
            OK
          </button>
          <button
            onClick={onClose}
            aria-label="Cancel selection"
            style={{
              padding: "10px 20px",
              backgroundColor: "#dc2626",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "1.1rem",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              boxShadow: "4px 4px 12px rgba(0, 0, 0, 0.3)",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#b91c1c")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#dc2626")}
          >
            Cancel
          </button>
        </div>
      </div>
      {showUploadModal && (
        <BackgroundUploadModal
          onUpload={handleNewUpload}
          onClose={() => setShowUploadModal(false)}
        />
      )}
    </div>
  );
};

export default BackgroundSelector;